import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2 } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import PhotoLightbox from "./PhotoLightbox";

/**
 * Read-only grid of a job's photos, every tile opening the lightbox.
 *
 * One component for every surface that *shows* photos — the job page in both
 * namespaces and the public job — because the three had drifted to three
 * different column counts and none of them could be opened. The editing
 * surface is JobPhotoGrid; a tile there already carries Replace and Remove,
 * and a third tap target on the same square is how the wrong photo gets
 * deleted.
 *
 * The columns match JobPhotoGrid's on purpose: on the job page the grid must
 * not reflow when the owner switches between reading and editing.
 *
 * Props:
 *   urls: string[]   photo urls, in display order
 *   testid: string
 */
export default function JobPhotoGallery({ urls = [], testid = "job-photos" }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);

  if (!urls.length) return null;

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={testid}>
        {urls.map((url, i) => (
          <button
            key={`${url}-${i}`}
            type="button"
            onClick={() => setOpen(i)}
            aria-label={t("lightbox.openPhoto", { current: i + 1, total: urls.length })}
            className="relative block aspect-square rounded-sm border border-sand bg-sand overflow-hidden ring-hive transition-transform hover:-translate-y-0.5 hover:border-hive"
            data-testid={`${testid}-${i}`}
          >
            <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" />
            {/* **The tile is a square crop of a photo that is not square**, so
                what a FixyBee sees here is never the whole picture — and
                nothing said it could be opened. T18-M1, from Daniel quoting on
                a phone: the viewer was already behind this tile, already
                full-screen, and he reported not being able to see the photo.
                A control that only exists once you guess it is there is a
                control that is missing. The aria-label was always on the
                button; this is the same sentence, drawn. */}
            <span
              aria-hidden="true"
              className="absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-worker/70 text-cream"
            >
              <Maximize2 size={14} />
            </span>
          </button>
        ))}
      </div>
      <PhotoLightbox
        urls={urls}
        index={open}
        onIndex={setOpen}
        onClose={() => setOpen(null)}
      />
    </>
  );
}
